import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Instance } from '../services/apiService';
import ConfirmDialog from '../components/ConfirmDialog';

// Live timer component for running instances
const LiveQuotaTimer: React.FC<{ instance: Instance; totalHours: number }> = ({ instance, totalHours }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (instance.State !== 'running') return;
    
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [instance.State]);

  const calculateRuntime = () => {
    const totalSeconds = instance.RuntimeSeconds || 0;
    const lastStart = instance.LastStartTime || 0;
    
    if (instance.State === 'running' && lastStart > 0) {
      const currentSession = Math.floor((currentTime / 1000) - lastStart);
      return totalSeconds + currentSession;
    }
    return totalSeconds;
  };

  const runtimeSeconds = calculateRuntime();
  const hours = Math.floor(runtimeSeconds / 3600);
  const minutes = Math.floor((runtimeSeconds % 3600) / 60);
  const seconds = runtimeSeconds % 60;
  const usedHours = runtimeSeconds / 3600;
  const percentage = (usedHours / totalHours) * 100;
  const isOverQuota = usedHours >= totalHours;
  const isWarning = percentage > 80;
  
  // Format quota hours properly
  const quotaHours = Math.floor(totalHours);
  const quotaMinutes = Math.floor((totalHours % 1) * 60);

  return (
    <div style={{ minWidth: '180px' }}>
      <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.4rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
        ⏱️ {hours}h {minutes}m {seconds}s / {quotaHours}h {quotaMinutes > 0 ? `${quotaMinutes}m` : ''}
      </div>
      <div style={{
        width: '100%',
        height: '4px',
        background: '#F5F7FA',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${Math.min(percentage, 100)}%`,
          height: '100%',
          background: isOverQuota
            ? 'linear-gradient(135deg, #FF6600, #FF8533)'
            : isWarning
            ? 'linear-gradient(135deg, #FF6600, #FFAA33)'
            : 'linear-gradient(135deg, #10b981, #059669)',
          borderRadius: '2px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { userEmail, logout } = useAuth();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'Tutorials' | 'Assignment1' | 'Assignment2'>('Tutorials');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [ipAddress, setIpAddress] = useState('');
  const [ipLoading, setIpLoading] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    instanceId: string;
    action: 'start' | 'stop' | 'force-stop';
    instanceName: string;
  }>({
    isOpen: false,
    instanceId: '',
    action: 'start',
    instanceName: ''
  });

  // Add QUT brand styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      body { background: linear-gradient(135deg, #003A70 0%, #0066CC 100%); min-height: 100vh; }
      .modern-btn:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
      }
      .start-btn:not(:disabled):hover {
        background: linear-gradient(135deg, #0066CC, #4A90E2) !important;
      }
      .stop-btn:not(:disabled):hover {
        background: linear-gradient(135deg, #FF6600, #FF8533) !important;
      }
      .modern-btn:active { transform: translateY(0); }
      .glass-card {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        box-shadow: 0 20px 40px rgba(0, 58, 112, 0.1);
      }
      .modern-table tr:hover {
        background: rgba(79, 172, 254, 0.05);
        transition: all 0.2s ease;
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    fetchInstances();
    // No need for separate quota fetch - ListInstances returns quota data
  }, []);

  // Auto-refresh: 5 seconds for pending states, 5 minutes for running
  useEffect(() => {
    const hasPendingInstances = instances.some(instance => 
      ['pending', 'stopping', 'starting'].includes(instance.State)
    );
    const hasRunningInstances = instances.some(instance => instance.State === 'running');
    
    if (!hasPendingInstances && !hasRunningInstances) return;

    const interval = hasPendingInstances ? 5000 : 300000; // 5s or 5min
    const intervalId = setInterval(() => {
      fetchInstances();
    }, interval);

    return () => clearInterval(intervalId);
  }, [instances]);

  // Auto-stop instances when quota is exceeded
  useEffect(() => {
    if (!instances.length) return;

    instances.forEach(async (instance) => {
      if (instance.State === 'running' && instance.Expired) {
        // Instance marked as expired by QuotaEnforcer
        setError(`Instance ${instance.Name || instance.InstanceId} was automatically stopped due to quota exceeded.`);
        fetchInstances(); // Refresh to show updated state
      }
    });
  }, [instances]);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      setError(null);
      const instancesData = await apiService.fetchInstances();
      setInstances(instancesData);
    } catch (error: any) {
      console.error('Failed to fetch instances:', error);
      setError(error.message || 'Failed to load instances. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotaInfo = async () => {
    try {
      const quota = await apiService.getQuotaInfo();
      setQuotaInfo(quota);
    } catch (error) {
      console.error('Failed to fetch quota info:', error);
    }
  };

  const handleInstanceAction = async (instanceId: string, action: 'start' | 'stop' | 'force-stop') => {
    const instance = instances.find(i => i.InstanceId === instanceId);
    setConfirmDialog({
      isOpen: true,
      instanceId,
      action,
      instanceName: instance?.Name || instanceId
    });
  };

  const confirmAction = async () => {
    const { instanceId, action } = confirmDialog;
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    // Check quota before starting
    if (action === 'start') {
      const quota = getInstanceQuota(instanceId);
      if (quota && quota.usedHours >= quota.totalHours) {
        setError('Cannot start instance: Quota exceeded. Please contact admin for more hours.');
        return;
      }
    }

    try {
      setActionLoading(`${instanceId}-${action}`);
      
      if (action === 'start') {
        await apiService.startInstance(instanceId);
      } else if (action === 'force-stop') {
        await apiService.forceStopInstance(instanceId);
      } else {
        await apiService.stopInstance(instanceId);
      }
      
      await fetchInstances();
    } catch (error) {
      console.error(`Failed to ${action} instance:`, error);
      alert(`Failed to ${action} instance. ${action === 'stop' ? 'Try using Force Stop.' : 'Please try again.'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const cancelAction = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const handleDownloadKeypair = async (instanceId: string) => {
    try {
      if (!userEmail) {
        alert('User email not available');
        return;
      }
      
      const data = await apiService.downloadKeypair(instanceId, userEmail);
      
      // Download the file
      const fileResponse = await fetch(data.downloadUrl);
      const fileContent = await fileResponse.text();
      
      // Determine file type and MIME type
      const isRdpFile = data.fileType === 'rdp' || data.keyName.endsWith('.rdp');
      const mimeType = isRdpFile ? 'application/x-rdp' : 'application/x-pem-file';
      
      // Create and download file
      const blob = new Blob([fileContent], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.keyName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      if (isRdpFile) {
        alert('RDP file downloaded! Double-click to connect. Password: AdminPassword123!');
      } else {
        alert('SSH key downloaded! Set permissions: chmod 400 ' + data.keyName);
      }
      
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleAddIP = async () => {
    if (!selectedInstance) {
      alert('No instance selected');
      return;
    }

    let finalIpAddress = ipAddress.trim();
    
    // If no IP provided, try to auto-detect
    if (!finalIpAddress) {
      try {
        finalIpAddress = await apiService.getMyIP();
        setIpAddress(finalIpAddress); // Update the input field
      } catch (error) {
        alert('Could not detect your IP address. Please enter it manually.');
        return;
      }
    }

    // Validate IP format
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(finalIpAddress)) {
      alert('Please enter a valid IP address (e.g., 192.168.1.100)');
      return;
    }

    try {
      setIpLoading(true);
      await apiService.addIPToSecurityGroup(selectedInstance.InstanceId, finalIpAddress);
      alert(`Successfully added ${finalIpAddress}/32 to security group for SSH and RDP access`);
      setIpAddress('');
    } catch (error) {
      console.error('Failed to add IP:', error);
      alert(`Failed to add IP: ${error}`);
    } finally {
      setIpLoading(false);
    }
  };

  // Auto-detect IP when modal opens
  const handleInstanceSelect = async (instance: Instance) => {
    setSelectedInstance(instance);
    try {
      const detectedIP = await apiService.getMyIP();
      setIpAddress(detectedIP);
    } catch (error) {
      setIpAddress('');
    }
  };

  const getInstanceQuota = (instanceId: string) => {
    // Get quota directly from instance data (returned by ListInstances Lambda)
    const instance = instances.find(i => i.InstanceId === instanceId);
    if (!instance) return null;
    
    const usedHours = instance.RuntimeHours || 0;
    const totalHours = instance.QuotaHours || 10;
    
    return { usedHours, totalHours };
  };

  const getButtonState = (instance: Instance, action: 'start' | 'stop') => {
    const isTransitioning = ['pending', 'stopping', 'starting'].includes(instance.State);
    const isLoading = actionLoading === `${instance.InstanceId}-${action}`;
    const quota = getInstanceQuota(instance.InstanceId);
    const isOverQuota = quota && quota.usedHours >= quota.totalHours;
    
    // Debug logging for student5

    
    if (action === 'start') {
      return {
        disabled: Boolean(instance.State !== 'stopped' || isTransitioning || isLoading || isOverQuota),
        text: isOverQuota ? '❌ Quota Exceeded' : isLoading ? '⏳ Starting...' : '▶️ Start',
        className: 'btn btn-success btn-sm'
      };
    } else {
      return {
        disabled: Boolean(instance.State !== 'running' || isTransitioning || isLoading),
        text: isLoading ? '⏳ Stopping...' : '⏹️ Stop',
        className: 'btn btn-danger btn-sm'
      };
    }
  };

  const getFilteredInstances = () => {
    return instances.filter(instance => {
      const tags = instance.Tags || [];
      const categoryTag = tags.find(tag => tag.Key === 'Category');
      return categoryTag?.Value === activeTab;
    });
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #003A70 0%, #0066CC 50%, #4A90E2 100%)',
      position: 'relative'
    }}>
      
      <div style={{ position: 'relative', zIndex: 1, padding: '2rem' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Error Display */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '16px',
            padding: '1rem 1.5rem',
            marginBottom: '2rem',
            color: '#dc2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                cursor: 'pointer',
                fontSize: '1.2rem',
                padding: '0.25rem'
              }}
            >
              ✕
            </button>
          </div>
        )}
        {/* Premium Header */}
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1))',
          backdropFilter: 'blur(30px)',
          borderRadius: '32px',
          padding: '3rem',
          marginBottom: '3rem',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
          position: 'relative',
          overflow: 'hidden',
          animation: 'glow 4s ease-in-out infinite'
        }}>
          {/* Premium gradient overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(240,147,251,0.1) 50%, rgba(255,255,255,0.1) 100%)',
            pointerEvents: 'none'
          }} />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '3.5rem',
              fontWeight: '800',
              color: 'white',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5), 0 2px 10px rgba(102, 126, 234, 0.8)',
              letterSpacing: '-0.02em'
            }}>
              ✨ Student Portal
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', position: 'relative', zIndex: 1 }}>
            <div style={{ 
              padding: '1rem 2rem',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              color: '#003A70',
              fontWeight: '600',
              fontSize: '1.1rem',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(15px)'
            }}>
              👋 {userEmail}
            </div>
            <button 
              onClick={logout}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #FF6600, #FF8533)',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '700',
                boxShadow: '0 10px 30px rgba(220, 38, 38, 0.5)',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(15px)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(220, 38, 38, 0.7)';
                e.currentTarget.style.background = 'linear-gradient(135deg, #FF6600, #FF8533)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(220, 38, 38, 0.5)';
                e.currentTarget.style.background = 'linear-gradient(135deg, #FF6600, #FF8533)';
              }}
            >
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="glass-card" style={{ 
          borderRadius: '24px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '2rem',
            background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1), rgba(0, 242, 254, 0.05))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ 
                margin: 0, 
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#003A70'
              }}>
                💻 Your EC2 Instances
              </h2>
              <p style={{ 
                margin: '0.5rem 0 0 0',
                color: '#6b7280'
              }}>
                Monitor and control your virtual machines
              </p>
            </div>
            <button 
              onClick={fetchInstances}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                background: loading 
                  ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' 
                  : 'linear-gradient(135deg, #003A70, #0066CC)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                boxShadow: loading 
                  ? 'none' 
                  : '0 4px 12px rgba(79, 172, 254, 0.3)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(79, 172, 254, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.3)';
                }
              }}
            >
              🔄 {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div style={{ padding: '2rem' }}>
            {/* Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginBottom: '2rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {(['Tutorials', 'Assignment1', 'Assignment2'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: activeTab === tab 
                      ? 'linear-gradient(135deg, #003A70, #0066CC)' 
                      : 'transparent',
                    color: activeTab === tab ? 'white' : '#6b7280',
                    border: 'none',
                    borderRadius: '12px 12px 0 0',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    borderBottom: activeTab === tab ? '3px solid #003A70' : '3px solid transparent'
                  }}
                >
                  {tab === 'Assignment1' ? 'Assignment 1' : tab === 'Assignment2' ? 'Assignment 2' : tab}
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '4rem',
                color: '#6b7280'
              }}>
                <div style={{ 
                  fontSize: '3rem',
                  marginBottom: '1rem',
                  animation: 'spin 2s linear infinite'
                }}>
                  ⚙️
                </div>
                <p style={{ fontSize: '1.1rem' }}>Loading your instances...</p>
              </div>
            ) : getFilteredInstances().length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '4rem',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📦</div>
                <p style={{ fontSize: '1.1rem' }}>No instances found for {activeTab === 'Assignment1' ? 'Assignment 1' : activeTab === 'Assignment2' ? 'Assignment 2' : activeTab}</p>
              </div>
            ) : activeTab !== 'Tutorials' ? (
              // Assignment 1 & 2: Only show IP management
              <div style={{ 
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1))',
                backdropFilter: 'blur(30px)',
                borderRadius: '32px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                padding: '2rem'
              }}>
                <h3 style={{ marginBottom: '1.5rem', color: '#003A70' }}>🔒 IP Address Management</h3>
                {getFilteredInstances().map(instance => (
                  <div key={instance.InstanceId} style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    marginBottom: '1rem',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontWeight: '600', color: '#003A70', fontSize: '1.1rem' }}>
                        {instance.Name}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={selectedInstance?.InstanceId === instance.InstanceId ? ipAddress : ''}
                        onChange={(e) => setIpAddress(e.target.value)}
                        onFocus={() => handleInstanceSelect(instance)}
                        placeholder="Your IP will be auto-detected..."
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: '2px solid #F5F7FA',
                          borderRadius: '12px',
                          fontSize: '0.9rem',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={() => {
                          setSelectedInstance(instance);
                          handleAddIP();
                        }}
                        disabled={ipLoading}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: ipLoading ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' : 'linear-gradient(135deg, #003A70, #0066CC)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: ipLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {ipLoading ? '⏳ Adding...' : '🔒 Add My IP'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1))',
                backdropFilter: 'blur(30px)',
                borderRadius: '32px',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {/* Premium gradient overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(240,147,251,0.1) 50%, rgba(255,255,255,0.1) 100%)',
                  pointerEvents: 'none'
                }} />
                
                <div style={{ overflowX: 'auto', position: 'relative', zIndex: 1, padding: '2rem' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  background: 'transparent'
                }}>
                  <thead>
                    <tr style={{ 
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(240, 147, 251, 0.2))',
                      borderBottom: '2px solid rgba(255, 255, 255, 0.4)'
                    }}>
                      <th style={{ 
                        padding: '1.25rem', 
                        textAlign: 'left', 
                        fontWeight: '700', 
                        color: '#374151',
                        fontSize: '0.9rem',
                        letterSpacing: '0.05em'
                      }}>
                        🖥️ INSTANCE
                      </th>
                      <th style={{ 
                        padding: '1.25rem', 
                        textAlign: 'left', 
                        fontWeight: '700', 
                        color: '#374151',
                        fontSize: '0.9rem',
                        letterSpacing: '0.05em'
                      }}>
                        🔄 STATE
                      </th>
                      <th style={{ 
                        padding: '1.25rem', 
                        textAlign: 'left', 
                        fontWeight: '700', 
                        color: '#374151',
                        fontSize: '0.9rem',
                        letterSpacing: '0.05em'
                      }}>
                        🌐 PUBLIC IP
                      </th>
                      <th style={{ 
                        padding: '1.25rem', 
                        textAlign: 'left', 
                        fontWeight: '700', 
                        color: '#374151',
                        fontSize: '0.9rem',
                        letterSpacing: '0.05em'
                      }}>
                        ⏱️ QUOTA
                      </th>
                      <th style={{ 
                        padding: '1.25rem', 
                        textAlign: 'left', 
                        fontWeight: '700', 
                        color: '#374151',
                        fontSize: '0.9rem',
                        letterSpacing: '0.05em'
                      }}>
                        🎮 ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredInstances().map(instance => {
                      const startBtn = getButtonState(instance, 'start');
                      const stopBtn = getButtonState(instance, 'stop');
                      
                      return (
                        <tr key={instance.InstanceId} style={{ 
                          borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}>
                          <td style={{ padding: '1.5rem 1.25rem' }}>
                            <div style={{ 
                              fontWeight: '600', 
                              color: '#003A70',
                              fontSize: '1rem',
                              marginBottom: '0.25rem'
                            }}>
                              {instance.Name}
                            </div>
                            <div style={{ 
                              fontSize: '0.8rem', 
                              color: '#6b7280', 
                              fontFamily: 'monospace',
                              background: 'rgba(107, 114, 128, 0.1)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '6px',
                              display: 'inline-block'
                            }}>
                              {instance.InstanceId}
                            </div>
                          </td>
                          <td style={{ padding: '1.5rem 1.25rem' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '0.5rem 1rem',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              backgroundColor: 
                                instance.State === 'running' ? 'rgba(34, 197, 94, 0.1)' : 
                                instance.State === 'stopped' ? 'rgba(251, 191, 36, 0.1)' : 
                                'rgba(59, 130, 246, 0.1)',
                              color: 
                                instance.State === 'running' ? '#22c55e' : 
                                instance.State === 'stopped' ? '#fbbf24' : 
                                '#3b82f6',
                              border: `2px solid ${
                                instance.State === 'running' ? '#22c55e' : 
                                instance.State === 'stopped' ? '#fbbf24' : 
                                '#3b82f6'
                              }`,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              whiteSpace: 'nowrap'
                            }}>
                              {instance.State === 'running' ? '🟢' : 
                               instance.State === 'stopped' ? '🟡' : '🔵'} {instance.State}
                            </span>
                          </td>
                          <td style={{ padding: '1.5rem 1.25rem' }}>
                            <div style={{
                              padding: '0.5rem 1rem',
                              background: instance.PublicIp ? 'rgba(34, 197, 94, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                              color: instance.PublicIp ? '#22c55e' : '#6b7280',
                              borderRadius: '12px',
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              fontFamily: 'monospace',
                              border: `1px solid ${instance.PublicIp ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)'}`,
                              whiteSpace: 'nowrap'
                            }}>
                              🌐 {instance.PublicIp || 'Not Available'}
                            </div>
                          </td>
                          <td style={{ padding: '1.5rem 1.25rem' }}>
                            {(() => {
                              const quota = getInstanceQuota(instance.InstanceId);
                              if (!quota) return (
                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                  ⏱️ No data
                                </div>
                              );
                              
                              return <LiveQuotaTimer instance={instance} totalHours={quota.totalHours} />;
                            })()}
                          </td>
                          <td style={{ padding: '1.5rem 1.25rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button 
                                onClick={() => handleInstanceAction(instance.InstanceId, 'start')}
                                disabled={startBtn.disabled}
                                className="modern-btn start-btn"
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: startBtn.disabled 
                                    ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' 
                                    : 'linear-gradient(135deg, #003A70, #0066CC)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '12px',
                                  cursor: startBtn.disabled ? 'not-allowed' : 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  boxShadow: startBtn.disabled 
                                    ? 'none' 
                                    : '0 4px 12px rgba(79, 172, 254, 0.3)',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                              >
                                {startBtn.text}
                              </button>
                              {(() => {
                                const showForceStop = instance.State === 'stopping';
                                return (
                                  <>
                                    <button 
                                      onClick={() => handleInstanceAction(instance.InstanceId, 'stop')}
                                      disabled={stopBtn.disabled}
                                      className="modern-btn stop-btn"
                                      style={{
                                        padding: '0.5rem 1rem',
                                        background: stopBtn.disabled 
                                          ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' 
                                          : 'linear-gradient(135deg, #FF6600, #FF8533)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: stopBtn.disabled ? 'not-allowed' : 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        boxShadow: stopBtn.disabled 
                                          ? 'none' 
                                          : '0 4px 12px rgba(255, 107, 107, 0.3)',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                      }}
                                    >
                                      {stopBtn.text}
                                    </button>
                                    {showForceStop && (
                                      <button 
                                        onClick={() => handleInstanceAction(instance.InstanceId, 'force-stop')}
                                        className="modern-btn force-stop-btn"
                                        style={{
                                          padding: '0.5rem 1rem',
                                          background: 'linear-gradient(135deg, #DC2626, #EF4444)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '12px',
                                          cursor: 'pointer',
                                          fontSize: '0.8rem',
                                          fontWeight: '600',
                                          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                                          transition: 'all 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.25rem'
                                        }}
                                      >
                                        ⚠️ Force Stop
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                              {activeTab === 'Tutorials' && (
                                <button 
                                  onClick={() => handleDownloadKeypair(instance.InstanceId)}
                                  className="modern-btn keypair-btn"
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'linear-gradient(135deg, #ffd93d, #ffcd02)',
                                    color: '#92400e',
                                    border: 'none',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 12px rgba(255, 217, 61, 0.3)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 217, 61, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 217, 61, 0.3)';
                                  }}
                                >
                                  {(instance.Name?.includes('Windows') || instance.Name?.includes('windows') || instance.InstanceId === 'i-079d9443c979f9dd7') ? '🖥️ Download RDP' : '🔑 SSH Key'}
                                </button>
                              )}
                              <button 
                                onClick={() => handleInstanceSelect(instance)}
                                className="modern-btn details-btn"
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'linear-gradient(135deg, #a855f7, #9333ea)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '12px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(168, 85, 247, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(168, 85, 247, 0.3)';
                                }}
                              >
                                🔧 Manage
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Guide */}
        <div className="glass-card" style={{ 
          marginTop: '2rem',
          borderRadius: '24px',
          padding: '2rem'
        }}>
          <h3 style={{ 
            margin: '0 0 1.5rem 0', 
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: '700'
          }}>
            📚 Instance Guide
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '1.5rem', 
            fontSize: '0.95rem' 
          }}>
            <div style={{ 
              padding: '1rem',
              background: 'rgba(34, 197, 94, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 197, 94, 0.2)'
            }}>
              <strong>🟢 Running:</strong> Instance is active and ready to use
            </div>
            <div style={{ 
              padding: '1rem',
              background: 'rgba(251, 191, 36, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(251, 191, 36, 0.2)'
            }}>
              <strong>🟡 Stopped:</strong> Instance is stopped and can be started
            </div>
            <div style={{ 
              padding: '1rem',
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              <strong>🔵 Transitioning:</strong> Instance is changing state
            </div>
            <div style={{ 
              padding: '1rem',
              background: 'rgba(34, 197, 94, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 197, 94, 0.2)'
            }}>
              <strong>✅ Ready:</strong> All system checks passed
            </div>
            <div style={{ 
              padding: '1rem',
              background: 'rgba(251, 191, 36, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(251, 191, 36, 0.2)'
            }}>
              <strong>⏳ Checking:</strong> Status checks in progress
            </div>
            <div style={{ 
              padding: '1rem',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}>
              <strong>❌ Failed:</strong> System checks failed
            </div>
          </div>
        </div>



        {/* IP Management Modal */}
        {selectedInstance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => setSelectedInstance(null)}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '1.5rem', color: '#003A70' }}>🔒 IP Address Management</h3>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: '600', color: '#003A70', marginBottom: '0.5rem' }}>
                  {selectedInstance.Name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'monospace' }}>
                  {selectedInstance.InstanceId}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <input
                  type="text"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  placeholder="Your IP will be auto-detected..."
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '2px solid #F5F7FA',
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={handleAddIP}
                  disabled={ipLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: ipLoading ? 'linear-gradient(135deg, #F5F7FA, #d1d5db)' : 'linear-gradient(135deg, #003A70, #0066CC)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: ipLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {ipLoading ? '⏳ Adding...' : '🔒 Add My IP'}
                </button>
              </div>
              <button
                onClick={() => setSelectedInstance(null)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#F5F7FA',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#6b7280'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={`${confirmDialog.action === 'start' ? 'Start' : 'Stop'} Instance`}
          message={`Are you sure you want to ${confirmDialog.action} this instance?\n\n${confirmDialog.instanceName}\n(${confirmDialog.instanceId})`}
          confirmText={confirmDialog.action === 'start' ? 'Start Instance' : 'Stop Instance'}
          cancelText="Cancel"
          onConfirm={confirmAction}
          onCancel={cancelAction}
          type={confirmDialog.action === 'stop' ? 'danger' : 'info'}
        />
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
